import React, { useEffect, useState } from 'react';

const PaymentsReportPrint = () => {
    const [payments, setPayments] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo]     = useState('');

    useEffect(() => {
        const stored = localStorage.getItem('print_payments_report');
        if (stored) {
            const parsed = JSON.parse(stored);
            // Support both old format (plain array) and new format ({ payments, date_from, date_to })
            if (Array.isArray(parsed)) {
                setPayments(parsed);
            } else {
                setPayments(parsed.payments || []);
                setDateFrom(parsed.date_from || '');
                setDateTo(parsed.date_to || '');
            }
        }

        setTimeout(() => window.print(), 900);
    }, []);

    // ── Computed statistics ──────────────────────────────────────────────────
    const totalStudents = new Set(payments.map(p => p.student_id)).size;

    const totalBankOperations = payments
        .filter(p => {
            const name = (p.payment_mode_details?.name || p.payment_mode || '').toUpperCase();
            return !(name.includes('ESPECE') || name.includes('ESPÈCE'));
        })
        .reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);

    const totalEspece = payments
        .filter(p => {
            const name = (p.payment_mode_details?.name || p.payment_mode || '').toUpperCase();
            return name.includes('ESPECE') || name.includes('ESPÈCE');
        })
        .reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);

    const grandTotal = payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);

    const formatDate = (d) => {
        if (!d) return '--';
        try { return new Date(d).toLocaleDateString('fr-MA'); } catch { return d; }
    };

    const fmt = (n) => Number(n).toLocaleString('fr-MA', { minimumFractionDigits: 2 });

    return (
        <div className="report-wrapper" dir="rtl">

            {/* ══ Header ══════════════════════════════════════════════════════ */}
            <div className="report-header">
                <div className="header-logo-side">
                    <img src="/logo.png" alt="شعار الجمعية" className="header-logo" />
                    <div>
                        <p className="assoc-name">جمعية تنمية النقل المدرسي</p>
                        <p className="assoc-sub">ولاد دحو</p>
                    </div>
                </div>
                <div className="header-title-side">
                    <h1 className="report-title">لائحة المدفوعات</h1>
                    {dateFrom && dateTo && (
                        <p className="report-period">
                            من: <strong>{formatDate(dateFrom)}</strong> &nbsp;|&nbsp; إلى: <strong>{formatDate(dateTo)}</strong>
                        </p>
                    )}
                    <p className="report-print-date">تاريخ الطباعة: {new Date().toLocaleDateString('fr-MA')}</p>
                </div>
            </div>

            {/* ══ Main Table ══════════════════════════════════════════════════ */}
            <table className="main-table">
                <thead>
                    <tr>
                        <th className="col-num">#</th>
                        <th className="col-date">تاريخ الدفع</th>
                        <th className="col-receipt">رقم الوصل</th>
                        <th className="col-qr">QR</th>
                        <th className="col-name">الاسم الكامل</th>
                        <th className="col-amount">ثمن الأداء</th>
                        <th className="col-mode">وسيلة الدفع</th>
                        <th className="col-year">السنة الدراسية</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.length === 0 ? (
                        <tr><td colSpan="7" className="empty-row">لا توجد بيانات</td></tr>
                    ) : (
                        payments.map((p, idx) => (
                            <tr key={p.id} className={idx % 2 === 0 ? 'row-even' : 'row-odd'}>
                                <td className="td-center">{idx + 1}</td>
                                <td className="td-center">{formatDate(p.payment_date)}</td>
                                <td className="td-center receipt-num">{p.id}</td>
                                <td className="td-center td-qr">
                                    <span className="qr-code-text">{p.student?.qr_code || p.student?.code_massar || '--'}</span>
                                </td>
                                <td className="td-name">{p.student?.full_name || '--'}</td>
                                <td className="td-amount">{fmt(p.amount)} &nbsp;د.م</td>
                                <td className="td-center td-mode">
                                    {p.payment_mode_details?.name || p.payment_mode || '--'}
                                    {((p.payment_mode_details?.name?.toUpperCase()?.includes('CHEQUE') || p.payment_mode_details?.name?.includes('شيك') || p.payment_mode?.toUpperCase()?.includes('CHEQUE') || p.payment_mode?.includes('شيك')) && p.cheque_number) && (
                                        <div className="cheque-number">(الرقم : {p.cheque_number})</div>
                                    )}
                                </td>
                                <td className="td-center">{p.academic_year?.name || '--'}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* ══ Summary Table ═══════════════════════════════════════════════ */}
            <div className="summary-wrapper">
                <table className="summary-table">
                    <thead>
                        <tr>
                            <th colSpan="2" className="summary-header">ملخص اللائحة</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="sum-label">عدد التلاميذ الذين دفعوا</td>
                            <td className="sum-val">{totalStudents} تلميذ</td>
                        </tr>
                        <tr>
                            <td className="sum-label">عمليات بنكية</td>
                            <td className="sum-val">{fmt(totalBankOperations)} د.م</td>
                        </tr>
                        <tr>
                            <td className="sum-label">مجموع الدفع بـ نقد (ESPECE)</td>
                            <td className="sum-val">{fmt(totalEspece)} د.م</td>
                        </tr>
                        <tr className="sum-total-row">
                            <td className="sum-label">المجموع الكلي للمدفوعات</td>
                            <td className="sum-val sum-grand">{fmt(grandTotal)} د.م</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* ══ Footer ══════════════════════════════════════════════════════ */}
            <div className="report-footer">
                <div className="footer-sign">
                    <span>توقيع المحاسب</span>
                    <div className="sign-line" />
                </div>
                <div className="footer-sign">
                    <span>ختم الجمعية</span>
                    <div className="sign-line" />
                </div>
            </div>

            {/* ══ Styles ══════════════════════════════════════════════════════ */}
            <style dangerouslySetInnerHTML={{ __html: `
                @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');

                * { box-sizing: border-box; margin: 0; padding: 0; }

                body {
                    font-family: 'Cairo', sans-serif;
                    background: #fff;
                    color: #111;
                    direction: rtl;
                }

                .report-wrapper {
                    width: 210mm;
                    margin: 0 auto;
                    padding: 8mm 10mm;
                    direction: rtl;
                }

                /* ── Header ── */
                .report-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 2px solid #000;
                    padding-bottom: 8px;
                    margin-bottom: 10px;
                }
                .header-logo-side { display: flex; align-items: center; gap: 10px; }
                .header-logo { height: 55px; }
                .assoc-name { font-size: 13px; font-weight: 700; }
                .assoc-sub  { font-size: 11px; color: #555; }
                .header-title-side { text-align: left; }
                .report-title { font-size: 18px; font-weight: 700; }
                .report-period { font-size: 11px; margin-top: 3px; }
                .report-print-date { font-size: 10px; color: #666; margin-top: 2px; }

                /* ── Main Table ── */
                .main-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 11px;
                    margin-bottom: 14px;
                }
                .main-table th {
                    background: #1e293b;
                    color: #fff;
                    padding: 6px 5px;
                    text-align: center;
                    border: 1px solid #000;
                    font-size: 11px;
                }
                .main-table td {
                    border: 1px solid #666;
                    padding: 4px 5px;
                    vertical-align: middle;
                }
                .row-even { background: #fff; }
                .row-odd  { background: #f8fafc; }

                .col-num     { width: 26px; }
                .col-date    { width: 72px; }
                .col-receipt { width: 54px; }
                .col-qr      { width: 80px; }
                .col-name    { width: auto; }
                .col-amount  { width: 70px; }
                .col-mode    { width: 90px; }
                .col-year    { width: 72px; }

                .td-center  { text-align: center; }
                .td-qr      { text-align: center; }
                .qr-code-text { font-size: 9px; font-family: monospace; word-break: break-all; color: #1e293b; }
                .td-name    { font-weight: 600; }
                .td-amount  { text-align: center; font-weight: 700; }
                .td-mode    { font-size: 10px; font-weight: 600; color: #334155; }
                .cheque-number { font-size: 8px; color: #64748b; margin-top: 2px; }
                .receipt-num { font-weight: 700; }
                .empty-row  { text-align: center; padding: 20px; color: #999; }

                /* ── Summary Table ── */
                .summary-wrapper {
                    display: flex;
                    justify-content: flex-end;
                    margin-bottom: 16px;
                }
                .summary-table {
                    width: 55%;
                    border-collapse: collapse;
                    font-size: 12px;
                }
                .summary-header {
                    background: #1e293b;
                    color: #fff;
                    text-align: center;
                    padding: 6px 10px;
                    border: 1px solid #000;
                    font-size: 12px;
                }
                .summary-table td {
                    border: 1px solid #888;
                    padding: 5px 10px;
                }
                .sum-label { text-align: right; font-weight: 600; background: #f1f5f9; width: 65%; }
                .sum-val   { text-align: center; font-weight: 700; }
                .sum-total-row td { background: #e2e8f0 !important; }
                .sum-grand { font-size: 14px; }

                /* ── Footer ── */
                .report-footer {
                    display: flex;
                    justify-content: space-around;
                    margin-top: 20px;
                    padding-top: 10px;
                    border-top: 1px solid #ccc;
                }
                .footer-sign { text-align: center; font-size: 12px; font-weight: 600; }
                .sign-line { border-top: 1px solid #000; width: 100px; margin: 30px auto 0; }

                /* ── Print ── */
                @media print {
                    @page { size: A4 portrait; margin: 8mm; }
                    body  { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .report-wrapper { width: 100%; padding: 0; }
                }
            `}} />
        </div>
    );
};

export default PaymentsReportPrint;
